import React, { Component, type ErrorInfo, type ReactNode } from 'react'
import { recordBrowserErrorBoundary } from '@secureblob/sdk/browserRuntimeErrors'
import './ErrorBoundary.css'

interface Props {
    children: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
    errorInfo: ErrorInfo | null
    showDetails: boolean
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = { hasError: false, error: null, errorInfo: null, showDetails: false }
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error, errorInfo: null, showDetails: false }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught error:', error, errorInfo)
        // §389: write into the shared runtime-error buffer so production
        // triage and local debug snapshots both see React boundary failures.
        try {
            recordBrowserErrorBoundary(error, errorInfo)
        } catch {
            // Best-effort only — must never crash the render path.
        }
        this.setState({ error, errorInfo })
    }

    toggleDetails = () => {
        this.setState(prev => ({ showDetails: !prev.showDetails }))
    }

    handleReload = () => {
        window.location.reload()
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="container">
                    <div className="card">
                        <div className="header">
                            <div className="icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                    <line x1="12" y1="9" x2="12" y2="13" />
                                    <line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                            </div>
                            <h1 className="title">Something went wrong</h1>
                            <p className="description">
                                The application encountered an unexpected error.
                            </p>
                        </div>

                        <div className="actions">
                            <div className="button-group">
                                <button onClick={this.handleReload} className="reload-btn">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M23 4v6h-6" />
                                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                                    </svg>
                                    Reload Application
                                </button>
                                <button onClick={this.toggleDetails} className="details-btn">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: this.state.showDetails ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }}>
                                        <polyline points="6 9 12 15 18 9" />
                                    </svg>
                                    {this.state.showDetails ? 'Hide Details' : 'Show Details'}
                                </button>
                            </div>

                            {this.state.showDetails && (
                                <div className="details-panel">
                                    <div className="details-section">
                                        <div className="details-label">Error Message</div>
                                        <div className="error-message">
                                            {this.state.error?.toString()}
                                        </div>
                                    </div>

                                    {this.state.errorInfo?.componentStack && (
                                        <div className="details-section">
                                            <div className="details-label">Component Stack</div>
                                            <div className="code-block">
                                                {this.state.errorInfo.componentStack}
                                            </div>
                                        </div>
                                    )}

                                    {this.state.error?.stack && (
                                        <div className="details-section">
                                            <div className="details-label">Stack Trace</div>
                                            <div className="code-block">
                                                {this.state.error.stack}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}
